import React, { useEffect, useState } from 'react';
import { H1, UserContainer } from './style';
import axios from 'axios';
import AuthService from '../../../AuthService';
import { Input } from '../../Login/style';
import { toast } from 'react-toastify';
import { MdShoppingCart, MdPerson, MdDelete, MdEdit, MdAdd, MdClose, MdSearch, MdCheck } from 'react-icons/md';
import { FaEuroSign, FaBox } from 'react-icons/fa';

export default function Carts() {
    const [carts, setCarts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCart, setSelectedCart] = useState(null);
    const [showCartModal, setShowCartModal] = useState(false);
    const [editingCart, setEditingCart] = useState(false);

    // States para añadir producto
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);

    // States para cambiar usuario
    const [showChangeUser, setShowChangeUser] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [searchingUsers, setSearchingUsers] = useState(false);

    // States para editar cantidades y precios
    const [editingValues, setEditingValues] = useState({});

    // States para crear pedido
    const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
    const [orderShippingCosts, setOrderShippingCosts] = useState(6);
    const [orderPaymentMethod, setOrderPaymentMethod] = useState('Pago según convenio');
    const [orderNotes, setOrderNotes] = useState('');

    useEffect(() => {
        fetchCarts();
    }, [searchQuery]);

    const fetchCarts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`https://api.rb.beeapp.es/admin/carts?limit=100&search=${searchQuery}`, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });
            setCarts(response.data.carts || []);
        } catch (error) {
            console.error('Error fetching carts:', error);
            toast.error('Error al cargar carritos');
        } finally {
            setLoading(false);
        }
    };

    const fetchCartDetails = async (cartId) => {
        try {
            const response = await axios.get(`https://api.rb.beeapp.es/admin/carts/${cartId}`, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });
            setSelectedCart(response.data);
            setShowCartModal(true);
            setEditingValues({}); // Limpiar valores en edición al cargar un carrito
        } catch (error) {
            console.error('Error fetching cart details:', error);
            toast.error('Error al cargar detalles del carrito');
        }
    };

    const closeCartModal = () => {
        setShowCartModal(false);
        setSelectedCart(null);
        setShowAddProduct(false);
        setShowChangeUser(false);
        setEditingValues({}); // Limpiar valores en edición al cerrar
    };

    const searchProducts = async (query) => {
        if (!query || query.length < 2) {
            setProductResults([]);
            return;
        }

        setSearchingProducts(true);
        try {
            const response = await axios.get(`https://api.rb.beeapp.es/admin/search?s=${query}`, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });
            setProductResults(response.data.products?.productsSearch || []);
        } catch (error) {
            console.error('Error searching products:', error);
        } finally {
            setSearchingProducts(false);
        }
    };

    const searchUsers = async (query) => {
        if (!query || query.length < 2) {
            setUserResults([]);
            return;
        }

        setSearchingUsers(true);
        try {
            const response = await axios.get(`https://api.rb.beeapp.es/admin/search?s=${query}`, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });
            setUserResults(response.data.users?.usersSearch || []);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setSearchingUsers(false);
        }
    };

    const addProductToCart = async (productId) => {
        try {
            await axios.post(`https://api.rb.beeapp.es/admin/carts/${selectedCart._id}/items`, {
                productID: productId,
                quantity: 1
            }, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });

            toast.success('Producto añadido correctamente');
            setShowAddProduct(false);
            setProductSearch('');
            setProductResults([]);
            fetchCartDetails(selectedCart._id);
        } catch (error) {
            console.error('Error adding product:', error);
            toast.error(error.response?.data?.message || 'Error al añadir producto');
        }
    };

    // Funciones para manejar edición local
    const updateLocalQuantity = (itemId, value) => {
        setEditingValues(prev => ({
            ...prev,
            [itemId]: { ...prev[itemId], quantity: value }
        }));
    };

    const updateLocalPrice = (itemId, value) => {
        setEditingValues(prev => ({
            ...prev,
            [itemId]: { ...prev[itemId], price: value }
        }));
    };

    const confirmQuantityUpdate = async (itemId) => {
        const newQuantity = editingValues[itemId]?.quantity;
        if (!newQuantity || parseInt(newQuantity) < 1) {
            toast.error('La cantidad debe ser mayor a 0');
            return;
        }

        try {
            await axios.patch(`https://api.rb.beeapp.es/admin/carts/${selectedCart._id}/items/${itemId}/quantity`, {
                quantity: parseInt(newQuantity)
            }, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });

            toast.success('Cantidad actualizada');
            // Limpiar el estado de edición
            setEditingValues(prev => {
                const newState = { ...prev };
                if (newState[itemId]) {
                    delete newState[itemId].quantity;
                }
                return newState;
            });
            fetchCartDetails(selectedCart._id);
        } catch (error) {
            console.error('Error updating quantity:', error);
            toast.error('Error al actualizar cantidad');
        }
    };

    const confirmPriceUpdate = async (itemId) => {
        const newPrice = editingValues[itemId]?.price;

        try {
            await axios.patch(`https://api.rb.beeapp.es/admin/carts/${selectedCart._id}/items/${itemId}/price`, {
                customPrice: newPrice === '' || newPrice === null || newPrice === undefined ? null : parseFloat(newPrice)
            }, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });

            toast.success('Precio actualizado');
            // Limpiar el estado de edición
            setEditingValues(prev => {
                const newState = { ...prev };
                if (newState[itemId]) {
                    delete newState[itemId].price;
                }
                return newState;
            });
            fetchCartDetails(selectedCart._id);
        } catch (error) {
            console.error('Error updating price:', error);
            toast.error('Error al actualizar precio');
        }
    };

    const removeItem = async (itemId) => {
        if (!window.confirm('¿Eliminar este producto del carrito?')) return;

        try {
            await axios.delete(`https://api.rb.beeapp.es/admin/carts/${selectedCart._id}/items/${itemId}`, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });

            toast.success('Producto eliminado');
            fetchCartDetails(selectedCart._id);
        } catch (error) {
            console.error('Error removing item:', error);
            toast.error('Error al eliminar producto');
        }
    };

    const changeCartUser = async (newUserId) => {
        try {
            await axios.patch(`https://api.rb.beeapp.es/admin/carts/${selectedCart._id}/user`, {
                userID: newUserId
            }, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });

            toast.success('Usuario actualizado');
            setShowChangeUser(false);
            setUserSearch('');
            setUserResults([]);
            fetchCartDetails(selectedCart._id);
            fetchCarts();
        } catch (error) {
            console.error('Error changing user:', error);
            toast.error(error.response?.data?.message || 'Error al cambiar usuario');
        }
    };

    const openCreateOrderModal = () => {
        // Calcular costo de envío por defecto
        const total = selectedCart.estimatedTotal || 0;
        setOrderShippingCosts(total >= 100 ? 0 : 6);
        setOrderPaymentMethod('Pago según convenio');
        setOrderNotes('Pedido creado desde gestión de carritos por administrador');
        setShowCreateOrderModal(true);
    };

    const confirmCreateOrder = async () => {
        try {
            const response = await axios.post(
                `https://api.rb.beeapp.es/admin/carts/${selectedCart._id}/create-order`,
                {
                    clearCart: true,
                    paymentMethod: orderPaymentMethod,
                    notes: orderNotes,
                    shippingCosts: parseFloat(orderShippingCosts)
                },
                {
                    headers: {
                        'Authorization': 'Bearer ' + AuthService.getToken()
                    }
                }
            );

            toast.success(`Pedido creado correctamente: ${response.data.order.ConfirmationCode}`);
            setShowCreateOrderModal(false);
            closeCartModal();
            fetchCarts();
        } catch (error) {
            console.error('Error creating order:', error);
            toast.error(error.response?.data?.message || 'Error al crear pedido');
        }
    };

    const deleteCart = async (cartId) => {
        if (!window.confirm('¿Eliminar este carrito completamente?')) return;

        try {
            await axios.delete(`https://api.rb.beeapp.es/admin/carts/${cartId}`, {
                headers: {
                    'Authorization': 'Bearer ' + AuthService.getToken()
                }
            });

            toast.success('Carrito eliminado');
            closeCartModal();
            fetchCarts();
        } catch (error) {
            console.error('Error deleting cart:', error);
            toast.error('Error al eliminar carrito');
        }
    };

    return (
        <UserContainer>
            {/* Header */}
            <div style={{
                marginBottom: '32px'
            }}>
                <H1 style={{ color: '#028322', fontSize: '2rem', marginBottom: '8px', fontWeight: '600' }}>
                    Gestión de Carritos
                </H1>
                <p style={{ fontSize: '1rem', color: '#028322', margin: 0 }}>
                    Administra los carritos activos de los usuarios
                </p>
            </div>

            {/* Barra de búsqueda */}
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
            }}>
                <div style={{ position: 'relative' }}>
                    <MdSearch style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '20px',
                        color: '#9ca3af'
                    }} />
                    <Input
                        type="text"
                        placeholder="Buscar por nombre de usuario, email, organización..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            paddingLeft: '44px',
                            width: '100%',
                            fontSize: '14px'
                        }}
                    />
                </div>
            </div>

            {/* Estadísticas */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <MdShoppingCart size={32} color="#028322" />
                        <div>
                            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Carritos activos</p>
                            <p style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                                {carts.length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lista de carritos */}
            {loading ? (
                <div style={{
                    padding: '60px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        border: '4px solid #e5e7eb',
                        borderTop: '4px solid #028322',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto'
                    }} />
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
            ) : carts.length === 0 ? (
                <div style={{
                    padding: '60px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                }}>
                    <MdShoppingCart size={64} color="#d1d5db" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '18px', color: '#6b7280', margin: 0 }}>
                        No hay carritos activos
                    </p>
                </div>
            ) : (
                <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden'
                }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse'
                    }}>
                        <thead>
                            <tr style={{
                                backgroundColor: '#f9fafb',
                                borderBottom: '2px solid #e5e7eb'
                            }}>
                                <th style={{
                                    padding: '16px',
                                    textAlign: 'left',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>Usuario</th>
                                <th style={{
                                    padding: '16px',
                                    textAlign: 'left',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>Email</th>
                                <th style={{
                                    padding: '16px',
                                    textAlign: 'center',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>Items</th>
                                <th style={{
                                    padding: '16px',
                                    textAlign: 'right',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>Total estimado</th>
                                <th style={{
                                    padding: '16px',
                                    textAlign: 'center',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carts.map((cart, index) => (
                                <tr key={cart._id} style={{
                                    borderBottom: index < carts.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <td style={{
                                        padding: '16px',
                                        fontSize: '14px',
                                        color: '#1f2937'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <MdPerson size={20} color="#6b7280" />
                                            <span>{cart.userID?.Name || 'Sin usuario'}</span>
                                        </div>
                                    </td>
                                    <td style={{
                                        padding: '16px',
                                        fontSize: '14px',
                                        color: '#6b7280'
                                    }}>
                                        {cart.userID?.Email || '-'}
                                    </td>
                                    <td style={{
                                        padding: '16px',
                                        fontSize: '14px',
                                        color: '#1f2937',
                                        textAlign: 'center'
                                    }}>
                                        <span style={{
                                            backgroundColor: '#dbeafe',
                                            color: '#1e40af',
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}>
                                            {cart.totalItems} items
                                        </span>
                                    </td>
                                    <td style={{
                                        padding: '16px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#028322',
                                        textAlign: 'right'
                                    }}>
                                        {parseFloat(cart.estimatedTotal).toFixed(2)}€
                                    </td>
                                    <td style={{
                                        padding: '16px',
                                        textAlign: 'center'
                                    }}>
                                        <button
                                            onClick={() => fetchCartDetails(cart._id)}
                                            style={{
                                                backgroundColor: '#028322',
                                                color: '#fff',
                                                border: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <MdEdit size={16} />
                                            Gestionar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de gestión de carrito */}
            {showCartModal && selectedCart && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '20px'
                }}
                onClick={closeCartModal}
                >
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '16px',
                        maxWidth: '1100px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '32px',
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: '#fafafa'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div>
                                    <h2 style={{
                                        margin: 0,
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#111827',
                                        fontFamily: 'Inter, sans-serif',
                                        marginBottom: '8px'
                                    }}>
                                        Carrito de {selectedCart.userID?.Name || 'Usuario'}
                                    </h2>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        {selectedCart.userID?.Email}
                                        {selectedCart.userID?.Organization && ` • ${selectedCart.userID.Organization}`}
                                    </p>
                                </div>
                                <button
                                    onClick={closeCartModal}
                                    style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: '#fff',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        color: '#6b7280',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Resumen rápido */}
                            <div style={{
                                display: 'flex',
                                gap: '16px',
                                flexWrap: 'wrap'
                            }}>
                                <div style={{
                                    flex: '1',
                                    minWidth: '150px',
                                    backgroundColor: '#fff',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <p style={{
                                        margin: '0 0 4px 0',
                                        fontSize: '13px',
                                        color: '#6b7280',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        Total items
                                    </p>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#111827',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        {selectedCart.totalItems}
                                    </p>
                                </div>
                                <div style={{
                                    flex: '1',
                                    minWidth: '150px',
                                    backgroundColor: '#fff',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <p style={{
                                        margin: '0 0 4px 0',
                                        fontSize: '13px',
                                        color: '#6b7280',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        Total estimado
                                    </p>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#028322',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        {parseFloat(selectedCart.estimatedTotal).toFixed(2)}€
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '32px'
                        }}>
                            {/* Botones de acción */}
                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                marginBottom: '32px',
                                flexWrap: 'wrap'
                            }}>
                                <button
                                    onClick={() => {
                                        setShowAddProduct(!showAddProduct);
                                        setShowChangeUser(false);
                                    }}
                                    style={{
                                        flex: '1',
                                        minWidth: '180px',
                                        backgroundColor: '#028322',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '12px 20px',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#026a1b'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#028322'}
                                >
                                    <MdAdd size={18} />
                                    {showAddProduct ? 'Cerrar' : 'Añadir producto'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowChangeUser(!showChangeUser);
                                        setShowAddProduct(false);
                                    }}
                                    style={{
                                        flex: '1',
                                        minWidth: '180px',
                                        backgroundColor: '#3b82f6',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '12px 20px',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                >
                                    <MdPerson size={18} />
                                    {showChangeUser ? 'Cerrar' : 'Cambiar usuario'}
                                </button>
                                <button
                                    onClick={openCreateOrderModal}
                                    disabled={!selectedCart.items || selectedCart.items.length === 0}
                                    style={{
                                        flex: '1',
                                        minWidth: '180px',
                                        backgroundColor: selectedCart.items && selectedCart.items.length > 0 ? '#7c3aed' : '#9ca3af',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '12px 20px',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: selectedCart.items && selectedCart.items.length > 0 ? 'pointer' : 'not-allowed',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s',
                                        fontFamily: 'Inter, sans-serif',
                                        opacity: selectedCart.items && selectedCart.items.length > 0 ? 1 : 0.6
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedCart.items && selectedCart.items.length > 0) {
                                            e.target.style.backgroundColor = '#6d28d9';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedCart.items && selectedCart.items.length > 0) {
                                            e.target.style.backgroundColor = '#7c3aed';
                                        }
                                    }}
                                >
                                    <MdShoppingCart size={18} />
                                    Crear Pedido
                                </button>
                                <button
                                    onClick={() => deleteCart(selectedCart._id)}
                                    style={{
                                        flex: '0',
                                        minWidth: '140px',
                                        backgroundColor: '#dc2626',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '12px 20px',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                                >
                                    <MdDelete size={18} />
                                    Eliminar
                                </button>
                            </div>

                            {/* Panel de añadir producto */}
                            {showAddProduct && (
                                <div style={{
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    marginBottom: '24px'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 16px 0',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#111827',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        Buscar producto
                                    </h3>

                                    <Input
                                        type="text"
                                        placeholder="Escribe el nombre, referencia o EAN del producto..."
                                        value={productSearch}
                                        onChange={(e) => {
                                            setProductSearch(e.target.value);
                                            searchProducts(e.target.value);
                                        }}
                                        style={{
                                            marginBottom: '12px',
                                            fontSize: '15px',
                                            padding: '12px 16px'
                                        }}
                                    />

                                    {searchingProducts && (
                                        <div style={{
                                            padding: '20px',
                                            textAlign: 'center',
                                            color: '#6b7280',
                                            fontSize: '14px'
                                        }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                border: '3px solid #e5e7eb',
                                                borderTop: '3px solid #028322',
                                                borderRadius: '50%',
                                                animation: 'spin 0.8s linear infinite',
                                                margin: '0 auto 8px auto'
                                            }} />
                                            Buscando productos...
                                        </div>
                                    )}

                                    {!searchingProducts && productResults.length > 0 && (
                                        <div style={{
                                            maxHeight: '280px',
                                            overflowY: 'auto',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '10px',
                                            backgroundColor: '#f9fafb'
                                        }}>
                                            {productResults.map((product, index) => (
                                                <div
                                                    key={product._id}
                                                    onClick={() => addProductToCart(product._id)}
                                                    style={{
                                                        padding: '14px',
                                                        borderBottom: index < productResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '14px',
                                                        transition: 'all 0.2s',
                                                        backgroundColor: '#fff'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#f0fdf4';
                                                        e.currentTarget.style.transform = 'translateX(4px)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#fff';
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                    }}
                                                >
                                                    {product.images && product.images[0] ? (
                                                        <img
                                                            src={product.images[0]}
                                                            alt={product.name}
                                                            style={{
                                                                width: '52px',
                                                                height: '52px',
                                                                objectFit: 'cover',
                                                                borderRadius: '8px',
                                                                border: '2px solid #e5e7eb'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '52px',
                                                            height: '52px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#f3f4f6',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <FaBox size={20} color="#9ca3af" />
                                                        </div>
                                                    )}
                                                    <div style={{ flex: 1 }}>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '15px',
                                                            fontWeight: '600',
                                                            color: '#1f2937',
                                                            marginBottom: '4px'
                                                        }}>
                                                            {product.name}
                                                        </p>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '13px',
                                                            color: '#6b7280'
                                                        }}>
                                                            {product.reference && `Ref: ${product.reference} • `}
                                                            <span style={{ color: '#028322', fontWeight: '600' }}>
                                                                {product.price}€
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <MdAdd size={24} color="#028322" />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!searchingProducts && productSearch.length >= 2 && productResults.length === 0 && (
                                        <div style={{
                                            padding: '40px 20px',
                                            textAlign: 'center',
                                            color: '#6b7280',
                                            backgroundColor: '#f9fafb',
                                            borderRadius: '10px',
                                            border: '1px dashed #d1d5db'
                                        }}>
                                            <FaBox size={48} color="#d1d5db" style={{ marginBottom: '12px' }} />
                                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>
                                                No se encontraron productos
                                            </p>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                                                Intenta con otro término de búsqueda
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Panel de cambiar usuario */}
                            {showChangeUser && (
                                <div style={{
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    marginBottom: '24px'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 16px 0',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#111827',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        Cambiar usuario
                                    </h3>

                                    <Input
                                        type="text"
                                        placeholder="Busca por nombre, email o CIF..."
                                        value={userSearch}
                                        onChange={(e) => {
                                            setUserSearch(e.target.value);
                                            searchUsers(e.target.value);
                                        }}
                                        style={{
                                            marginBottom: '12px',
                                            fontSize: '15px',
                                            padding: '12px 16px'
                                        }}
                                    />

                                    {searchingUsers && (
                                        <div style={{
                                            padding: '20px',
                                            textAlign: 'center',
                                            color: '#6b7280',
                                            fontSize: '14px'
                                        }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                border: '3px solid #e5e7eb',
                                                borderTop: '3px solid #3b82f6',
                                                borderRadius: '50%',
                                                animation: 'spin 0.8s linear infinite',
                                                margin: '0 auto 8px auto'
                                            }} />
                                            Buscando usuarios...
                                        </div>
                                    )}

                                    {!searchingUsers && userResults.length > 0 && (
                                        <div style={{
                                            maxHeight: '280px',
                                            overflowY: 'auto',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '10px',
                                            backgroundColor: '#f9fafb'
                                        }}>
                                            {userResults.map((user, index) => (
                                                <div
                                                    key={user._id}
                                                    onClick={() => changeCartUser(user._id)}
                                                    style={{
                                                        padding: '14px',
                                                        borderBottom: index < userResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        transition: 'all 0.2s',
                                                        backgroundColor: '#fff'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#eff6ff';
                                                        e.currentTarget.style.transform = 'translateX(4px)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#fff';
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '44px',
                                                        height: '44px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#dbeafe',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <MdPerson size={24} color="#3b82f6" />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '15px',
                                                            fontWeight: '600',
                                                            color: '#1f2937',
                                                            marginBottom: '2px'
                                                        }}>
                                                            {user.Name} {user.Surname}
                                                        </p>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '13px',
                                                            color: '#6b7280'
                                                        }}>
                                                            {user.Email}
                                                            {user.Organization && ` • ${user.Organization}`}
                                                        </p>
                                                    </div>
                                                    <MdEdit size={20} color="#3b82f6" />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!searchingUsers && userSearch.length >= 2 && userResults.length === 0 && (
                                        <div style={{
                                            padding: '40px 20px',
                                            textAlign: 'center',
                                            color: '#6b7280',
                                            backgroundColor: '#f9fafb',
                                            borderRadius: '10px',
                                            border: '1px dashed #d1d5db'
                                        }}>
                                            <MdPerson size={48} color="#d1d5db" style={{ marginBottom: '12px' }} />
                                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>
                                                No se encontraron usuarios
                                            </p>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                                                Intenta con otro término de búsqueda
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Lista de productos */}
                            <div>
                                <h3 style={{
                                    margin: '0 0 20px 0',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#111827',
                                    fontFamily: 'Inter, sans-serif'
                                }}>
                                    Productos ({selectedCart.items?.length || 0})
                                </h3>

                                {selectedCart.items && selectedCart.items.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {selectedCart.items.map((item) => (
                                            <div key={item._id} style={{
                                                backgroundColor: '#f9fafb',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '12px',
                                                padding: '0',
                                                overflow: 'hidden',
                                                transition: 'all 0.2s'
                                            }}>
                                                {/* Header del producto */}
                                                <div style={{
                                                    padding: '16px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px'
                                                }}>
                                                    {item.productID?.images && item.productID.images[0] ? (
                                                        <img
                                                            src={item.productID.images[0]}
                                                            alt={item.productID.name}
                                                            style={{
                                                                width: '56px',
                                                                height: '56px',
                                                                objectFit: 'cover',
                                                                borderRadius: '8px',
                                                                border: '2px solid #e5e7eb'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '56px',
                                                            height: '56px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#e5e7eb',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <FaBox size={24} color="#9ca3af" />
                                                        </div>
                                                    )}

                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{
                                                            margin: 0,
                                                            fontSize: '15px',
                                                            fontWeight: '600',
                                                            color: '#111827',
                                                            lineHeight: '1.4',
                                                            fontFamily: 'Inter, sans-serif'
                                                        }}>
                                                            {item.productID?.name || 'Producto desconocido'}
                                                        </h4>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                            marginTop: '4px',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            {item.productID?.reference && (
                                                                <p style={{
                                                                    margin: 0,
                                                                    fontSize: '13px',
                                                                    color: '#6b7280'
                                                                }}>
                                                                    Ref: {item.productID.reference}
                                                                </p>
                                                            )}
                                                            {item.productID?.stock !== undefined && (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    backgroundColor: item.productID.stock > 0 ? '#d1fae5' : '#fee2e2',
                                                                    padding: '3px 10px',
                                                                    borderRadius: '6px'
                                                                }}>
                                                                    <FaBox size={12} color={item.productID.stock > 0 ? '#065f46' : '#dc2626'} />
                                                                    <span style={{
                                                                        fontSize: '12px',
                                                                        fontWeight: '600',
                                                                        color: item.productID.stock > 0 ? '#065f46' : '#dc2626'
                                                                    }}>
                                                                        Stock: {item.productID.stock}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => removeItem(item._id)}
                                                        style={{
                                                            backgroundColor: '#fee2e2',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            color: '#dc2626',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#fecaca'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#fee2e2'}
                                                    >
                                                        <MdDelete size={20} />
                                                    </button>
                                                </div>

                                                {/* Controles del producto */}
                                                <div style={{
                                                    padding: '20px',
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(80px, 120px) minmax(160px, 1fr) auto',
                                                    gap: '24px',
                                                    alignItems: 'start'
                                                }}>
                                                    {/* Cantidad */}
                                                    <div>
                                                        <label style={{
                                                            display: 'block',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            color: '#6b7280',
                                                            marginBottom: '8px',
                                                            fontFamily: 'Inter, sans-serif'
                                                        }}>
                                                            Cantidad
                                                        </label>
                                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={editingValues[item._id]?.quantity !== undefined ? editingValues[item._id].quantity : item.quantity}
                                                                onChange={(e) => updateLocalQuantity(item._id, e.target.value)}
                                                                style={{
                                                                    width: '70px',
                                                                    padding: '10px 12px',
                                                                    border: editingValues[item._id]?.quantity !== undefined ? '2px solid #028322' : '1px solid #d1d5db',
                                                                    borderRadius: '8px',
                                                                    fontSize: '15px',
                                                                    fontWeight: '600',
                                                                    outline: 'none',
                                                                    fontFamily: 'Inter, sans-serif',
                                                                    textAlign: 'center'
                                                                }}
                                                            />
                                                            {editingValues[item._id]?.quantity !== undefined && (
                                                                <button
                                                                    onClick={() => confirmQuantityUpdate(item._id)}
                                                                    style={{
                                                                        padding: '10px',
                                                                        backgroundColor: '#028322',
                                                                        border: 'none',
                                                                        borderRadius: '8px',
                                                                        color: '#fff',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0
                                                                    }}
                                                                >
                                                                    <MdCheck size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Precio unitario */}
                                                    <div>
                                                        <label style={{
                                                            display: 'block',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            color: '#6b7280',
                                                            marginBottom: '8px',
                                                            fontFamily: 'Inter, sans-serif'
                                                        }}>
                                                            Precio unitario
                                                            {item.customPrice !== null && (
                                                                <span style={{
                                                                    marginLeft: '6px',
                                                                    color: '#ea580c',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    (personalizado)
                                                                </span>
                                                            )}
                                                        </label>
                                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                            <div style={{ position: 'relative', width: '140px', flexShrink: 0 }}>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    placeholder={item.productID?.price || '0'}
                                                                    value={editingValues[item._id]?.price !== undefined ? editingValues[item._id].price : (item.customPrice !== null ? item.customPrice : '')}
                                                                    onFocus={(e) => {
                                                                        if (editingValues[item._id]?.price === undefined) {
                                                                            updateLocalPrice(item._id, e.target.value || item.customPrice || '');
                                                                        }
                                                                    }}
                                                                    onChange={(e) => updateLocalPrice(item._id, e.target.value)}
                                                                    onInput={(e) => updateLocalPrice(item._id, e.target.value)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '10px 35px 10px 12px',
                                                                        border: editingValues[item._id]?.price !== undefined ? '2px solid #028322' : (item.customPrice !== null ? '1px solid #ea580c' : '1px solid #d1d5db'),
                                                                        borderRadius: '8px',
                                                                        fontSize: '15px',
                                                                        fontWeight: '600',
                                                                        outline: 'none',
                                                                        fontFamily: 'Inter, sans-serif'
                                                                    }}
                                                                />
                                                                <span style={{
                                                                    position: 'absolute',
                                                                    right: '12px',
                                                                    top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    fontSize: '15px',
                                                                    fontWeight: '600',
                                                                    color: '#6b7280',
                                                                    pointerEvents: 'none'
                                                                }}>€</span>
                                                            </div>
                                                            {editingValues[item._id]?.price !== undefined && (
                                                                <button
                                                                    onClick={() => confirmPriceUpdate(item._id)}
                                                                    style={{
                                                                        padding: '10px',
                                                                        backgroundColor: '#028322',
                                                                        border: 'none',
                                                                        borderRadius: '8px',
                                                                        color: '#fff',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0,
                                                                        zIndex: 10,
                                                                        position: 'relative'
                                                                    }}
                                                                >
                                                                    <MdCheck size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <p style={{
                                                            margin: '6px 0 0 0',
                                                            fontSize: '12px',
                                                            color: '#9ca3af',
                                                            fontWeight: '500'
                                                        }}>
                                                            Original: <span style={{ color: '#6b7280', fontWeight: '600' }}>{item.productID?.price || '0'}€</span>
                                                        </p>
                                                    </div>

                                                    {/* Subtotal */}
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-end',
                                                        justifySelf: 'end'
                                                    }}>
                                                        <label style={{
                                                            display: 'block',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            color: '#6b7280',
                                                            marginBottom: '8px',
                                                            fontFamily: 'Inter, sans-serif'
                                                        }}>
                                                            Subtotal
                                                        </label>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '28px',
                                                            fontWeight: '700',
                                                            color: '#028322',
                                                            lineHeight: '1',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {item.subtotal}€
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '8px'
                                    }}>
                                        El carrito está vacío
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de configuración de pedido */}
            {showCreateOrderModal && selectedCart && (
                <div onClick={() => setShowCreateOrderModal(false)} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    padding: '20px'
                }}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: '500px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '24px',
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: '#fafafa'
                        }}>
                            <h2 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#111827',
                                margin: '0 0 8px 0',
                                fontFamily: 'Inter, sans-serif'
                            }}>
                                Crear Pedido
                            </h2>
                            <p style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                margin: 0,
                                fontFamily: 'Inter, sans-serif'
                            }}>
                                Configura las opciones del pedido antes de crearlo
                            </p>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '24px' }}>
                            {/* Método de pago */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '8px',
                                    fontFamily: 'Inter, sans-serif'
                                }}>
                                    Método de pago
                                </label>
                                <select
                                    value={orderPaymentMethod}
                                    onChange={(e) => setOrderPaymentMethod(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontFamily: 'Inter, sans-serif',
                                        outline: 'none'
                                    }}
                                >
                                    <option value="Pago según convenio">Pago según convenio</option>
                                    <option value="Transferencia Bancaria">Transferencia Bancaria</option>
                                </select>
                            </div>

                            {/* Costos de envío */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '8px',
                                    fontFamily: 'Inter, sans-serif'
                                }}>
                                    Costos de envío (€)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={orderShippingCosts}
                                    onChange={(e) => setOrderShippingCosts(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontFamily: 'Inter, sans-serif',
                                        outline: 'none'
                                    }}
                                />
                                <p style={{
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    marginTop: '6px',
                                    fontFamily: 'Inter, sans-serif'
                                }}>
                                    Total del pedido: {(parseFloat(selectedCart.estimatedTotal) || 0).toFixed(2)}€
                                </p>
                            </div>

                            {/* Notas */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '8px',
                                    fontFamily: 'Inter, sans-serif'
                                }}>
                                    Notas (opcional)
                                </label>
                                <textarea
                                    value={orderNotes}
                                    onChange={(e) => setOrderNotes(e.target.value)}
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontFamily: 'Inter, sans-serif',
                                        outline: 'none',
                                        resize: 'vertical'
                                    }}
                                    placeholder="Escribe notas adicionales para el pedido..."
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px'
                        }}>
                            <button
                                onClick={() => setShowCreateOrderModal(false)}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontFamily: 'Inter, sans-serif'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmCreateOrder}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#7c3aed',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontFamily: 'Inter, sans-serif'
                                }}
                            >
                                Crear Pedido
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </UserContainer>
    );
}
